/**
 * RunState cloning + serialization helpers.
 *
 * RunState is pure JSON-serializable data (no functions, no class instances —
 * the Rng class is only used transiently; we persist its plain RngState). So a
 * deep clone is safe and gives us immutable-ish reducer semantics: every public
 * reducer clones the input, mutates the clone, and returns it. Old references
 * remain valid (critical for save/undo/tests/determinism replays).
 *
 * We use a hand-rolled deep clone rather than structuredClone so this works in
 * every JS runtime (older Node, test envs) identically and predictably.
 */

import type { RunState } from "./types";

/**
 * Deep clone arbitrary JSON-like data (objects, arrays, primitives).
 * Throws on functions/symbols (which must never appear in RunState).
 */
export function deepClone<T>(value: T): T {
  if (value === null || typeof value !== "object") {
    if (typeof value === "function" || typeof value === "symbol") {
      throw new Error("deepClone: non-serializable value in state");
    }
    return value;
  }
  if (Array.isArray(value)) {
    const out = new Array(value.length);
    for (let i = 0; i < value.length; i++) {
      out[i] = deepClone(value[i]);
    }
    return out as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>)) {
    out[key] = deepClone((value as Record<string, unknown>)[key]);
  }
  return out as T;
}

/** Clone a RunState. */
export function cloneRun(run: RunState): RunState {
  return deepClone(run);
}

/**
 * Structural equality on RunState (used in determinism tests). Compares the
 * JSON-canonical form. Because we control key insertion order loosely, we sort
 * keys for a stable comparison.
 */
export function runEquals(a: RunState, b: RunState): boolean {
  return canonicalJson(a) === canonicalJson(b);
}

/** Deterministic JSON with sorted keys (stable hashing/equality). */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortDeep(value));
}

function sortDeep(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(sortDeep);
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    out[key] = sortDeep(obj[key]);
  }
  return out;
}
